import { useMountEffect } from "@react-hookz/web";
import React, { useMemo, useState } from "react";
import { CloseButton } from "scmp-sdk/components/acquisition-popup-container/display/popups/common/close-button";
import { PopupBackdrop } from "scmp-sdk/components/acquisition-popup-container/display/styles";
import type { PopupProps } from "scmp-sdk/components/acquisition-popup-container/types";
import { useSdkContext } from "scmp-sdk/components/contexts";
import { Modal } from "scmp-sdk/components/modal";
import { NewsletterWidget } from "scmp-sdk/components/newsletter/newsletter-widget";
import { Variant as NewsletterVariant } from "scmp-sdk/components/newsletter/newsletter-widget/enums";
import type { NewsletterItem } from "scmp-sdk/components/newsletter/types";

import { Container, ContentContainer, Description, ImageContainer, Title } from "./styles";
import type { AcquisitionNewsletter } from "./types";

export type Props = PopupProps<{
  acquisition: AcquisitionNewsletter;
}>;

export const AcquisitionNewsletterPopup: React.FunctionComponent<Props> = ({
  acquisition,
  modalContainerReference,
  onClose,
}) => {
  const { description, image, title } = acquisition.content;
  const newsletterItems = useMemo<NewsletterItem[]>(
    () =>
      (acquisition.tid
        ? [{ entityId: acquisition.tid, name: acquisition.name }]
        : acquisition.tids?.map(tid => ({ entityId: tid, name: acquisition.name }))) ?? [],
    [acquisition.name, acquisition.tid, acquisition.tids],
  );

  const { handleTrackGA4 } = useSdkContext();
  const [isSuccess, setIsSuccess] = useState(false);

  const campaignName = acquisition.campaign;

  useMountEffect(() => {
    handleTrackGA4({
      action: "imp",
      category: "promopopup",
      customized_parameters: {
        cta_type: "nl_signup",
        internal_campaign: campaignName,
        step: "1",
      },
      subcategory: "",
    });
  });

  return (
    <Modal
      BackdropComponent={PopupBackdrop}
      container={modalContainerReference?.current}
      fadeTimeout={400}
      open={true}
    >
      <Container>
        <CloseButton
          onClose={() => {
            handleTrackGA4({
              action: "click",
              category: "promopopup",
              customized_parameters: {
                cta_type: "nl_signup",
                internal_campaign: campaignName,
                message: "close button",
                step: "1",
              },
              subcategory: "",
            });
            onClose?.();
          }}
        />
        <ImageContainer>
          <img alt={title} src={image} />
        </ImageContainer>
        <ContentContainer>
          {!isSuccess && (
            <>
              <Title
                dangerouslySetInnerHTML={{
                  __html: title,
                }}
              />
              <Description
                dangerouslySetInnerHTML={{
                  __html: description,
                }}
              />
            </>
          )}
          <NewsletterWidget
            campaignName={campaignName}
            multipleNewsletters={true}
            newsletterItems={newsletterItems}
            onSuccess={() => {
              setIsSuccess(true);
            }}
            signUpButtonDisplayComponent={acquisition.button.text}
            variant={NewsletterVariant.AcquisitionNewsletterPopup}
          />
        </ContentContainer>
      </Container>
    </Modal>
  );
};

AcquisitionNewsletterPopup.displayName = "AcquisitionNewsletterPopup";
